import type { QueryParam } from '@exam-management/common/types';
import {
  QuestionGroupType,
  useSearchQuestionGroupsQuery,
  type QuestionGroupStatus,
} from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { useNavigate } from '@exam-management/router';
import { type GridCellParams, ErrorBox } from '@shared/components';

import { QContentListTable } from '../../components';
import { QuestionCategoryInput, initialQContentList } from '../../constants';
import { useQContentSearchParams } from '../../hooks';

import { columns } from './columns';

export default function ItemSetList() {
  const { qBankStructureInformation } = useQBankStructure();
  const [
    {
      page,
      pageSize,
      sort,
      qIds,
      category,
      part,
      subject,
      group,
      chapter,
      topic,
      subtopic,
      status,
    },
    setSearchParams,
  ] = useQContentSearchParams();
  const navigate = useNavigate();

  const levelItemId = subtopic || topic || chapter || group || subject || part;
  const questionGroupStatus = status as unknown as QuestionGroupStatus;
  const {
    data,
    loading: isLoading,
    error,
  } = useSearchQuestionGroupsQuery({
    variables: {
      search: {
        questionRefCode: qIds,
        refQBankStructureId: qBankStructureInformation.id,
        questionGroupType: [QuestionGroupType.ItemSet],
        levelItemId,
        status: questionGroupStatus ? [questionGroupStatus] : [],
      },
      paging: {
        pageSize,
        currentPage: page,
      },
      order: {
        field: 'updatedAt',
        type: sort,
      },
    },
    skip: category !== QuestionCategoryInput.ITEM_SET,
    fetchPolicy: 'network-only',
  });

  if (!category || category === QuestionCategoryInput.SINGLE_AND_LOQ) {
    return null;
  }

  const handleSearchQueryParams = (queryParams: QueryParam[]) => {
    setSearchParams(queryParams);
  };
  const rows = data?.qctSearchQuestionGroups.questionGroups ?? [];

  const mappedRows = rows.map((row) => {
    return {
      id: row.id,
      refCode: row.refCode,
      name: row.name,
      type: row.type,
      updatedAt: row.updatedAt,
      status: row.status,
      totalQuestions: row.totalQuestions,
    };
  });
  const itemSetCount = data?.qctSearchQuestionGroups.total ?? 0;

  const navigateToQContent = (cell: GridCellParams) => {
    if (cell.field !== 'moreAction')
      navigate('/qbank-structures/:structureId/qcontents/item-set/:questionId', {
        params: { structureId: qBankStructureInformation.id, questionId: cell.id.toString() },
      });
  };

  if (error) {
    return <ErrorBox />;
  }

  return (
    <QContentListTable
      columns={columns}
      rows={mappedRows}
      loading={isLoading}
      hideFooter={itemSetCount === 0 || isLoading}
      onCellClick={navigateToQContent}
      slotProps={{
        footer: {
          total: itemSetCount,
          pagination: {
            page: page,
            count: itemSetCount,
            rowsPerPage: pageSize,
            onPageChange: (_event, page) => {
              handleSearchQueryParams([{ name: 'page', value: page }]);
            },
            onRowsPerPageChange: (event) => {
              const pageSize =
                parseInt(event.target.value) === initialQContentList.pageSize
                  ? false
                  : event.target.value;

              return handleSearchQueryParams([
                {
                  name: 'pageSize',
                  value: pageSize,
                },
                { name: 'page', value: 0 },
              ]);
            },
          },
        },
      }}
      onSortModelChange={(sorting) => {
        const field = sorting[0]?.field;
        const sort = sorting[0]?.sort?.toLowerCase() ?? undefined;
        handleSearchQueryParams([
          { name: 'field', value: field },
          { name: 'sort', value: sort },
        ]);
      }}
    />
  );
}
